import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { RolePermissionService, SkylarkPermission } from 'app/_services/role-permission.service';
import { SiteWorkersService } from 'app/_services/site-workers.service';
import { SiteService } from 'app/_services/site.service';
import { InviteUsersService } from 'app/_services/invite-users.service';
import { CompetencyService } from 'app/_services/competency.service';
import { TrainingCourseState } from 'app/_models/training/training-course-state';
import { WorkerSiteCompetencyCounts } from 'app/_models/competency.model';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-invited-users',
  templateUrl: './invited-users.component.html',
})
export class InvitedUsersComponent implements OnInit {

  private static COMPETENCY_SHORT_NAMES = {
    'at authorised technician': 'AT',
    'at authorized technician': 'AT',
    'ct competent technician': 'CT',
    'safepass': 'SafePass',
    'safe pass': 'SafePass',
    'first aid': 'First Aid',
    'access': 'Access',
  };

  selectedSite: any;
  selectedSiteId: any;
  siteWorkers: any[] = [];
  routeRole: any;
  loading: boolean = true;

  public TrainingCourseStateType = TrainingCourseState;

  public columnOptionsSidebarVisible: boolean;

  public togglableColumns = [
    { displayName: 'Username', default: false },
    { displayName: 'Phone', default: false },
    { displayName: 'Company', default: true },
    { displayName: 'Competencies', default: true },
    { displayName: 'Asset Categories', default: true },
    { displayName: 'Guests Permitted', default: true },
    { displayName: 'Site Session Permitted', default: true },
    { displayName: 'Site Delegate', default: true },
  ];

  public tableOptions = {
    visibleColumns: [],
  };

  public hasInduction = false;

  public isPowerStationAdminView: boolean;
  public isWindFarmView: boolean;
  public isSiteDelegate: boolean;

  public competencyCountsByUser = new Map<string, WorkerSiteCompetencyCounts>();
  public hasReliableCompetencyCounts = false;

  private skylarkUserIds: number[] = [];

  constructor(
    private siteService: SiteService,
    private siteWorkersService: SiteWorkersService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private rolePermissionService: RolePermissionService,
    private inviteUsersService: InviteUsersService,
    private competencyService: CompetencyService,
    globals: Globals,
  ) {
    this.activatedRoute.params.subscribe((params) => {
      this.selectedSiteId = params.id;
      this.loadInvitedUserIdsFromStorage();
    });

    this.routeRole = globals.user.routeRole;
    this.isSiteDelegate = this.routeRole === 'site-delegate';

    const isAdmin = this.routeRole === 'admin' || this.routeRole === 'support';
    const isPowerStationView = this.rolePermissionService.hasPermission(SkylarkPermission.READ__POWER_STATION__WORKERS) && !isAdmin;
    this.isWindFarmView = !isPowerStationView;
    this.isPowerStationAdminView = this.rolePermissionService.hasPermission(SkylarkPermission.WRITE__POWER_STATION__ADMIN_DETAILS);
  }

  ngOnInit() {
    this.setupColumnOptions();
    this.getSelectedSite(this.selectedSiteId);
  }

  private loadInvitedUserIdsFromStorage() {
    const stored = localStorage.getItem('temp.data.invitedUsers');
    if (!stored) {
      this.skylarkUserIds = [];
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      // Only use stored ids if they belong to this site, otherwise discard
      if (parsed && String(parsed.siteId) === String(this.selectedSiteId) && Array.isArray(parsed.skylarkUserIds)) {
        this.skylarkUserIds = parsed.skylarkUserIds.map(Number);
      } else {
        this.skylarkUserIds = [];
      }
    } catch (e) {
      this.skylarkUserIds = [];
    }
  }

  public getSelectedSite(siteId) {
    this.siteService.getSiteById(siteId).subscribe((data) => {
      this.selectedSite = data;
      this.hasInduction = !!this.selectedSite.siteInductionCourse;
      this.loadInvitedUsers();
    });
  }

  private loadInvitedUsers() {
    if (!this.skylarkUserIds.length) {
      this.loading = false;
      return;
    }

    this.siteWorkersService.getWorkersByUserIds(this.selectedSiteId, this.skylarkUserIds)
      .pipe(finalize(() => { setTimeout(() => { this.loading = false; }, 500); }))
      .subscribe((data) => {
        this.siteWorkers = data as any[];

        this.siteWorkers.forEach((worker) => {
          worker.assetCategories.sort((a, b) => a.name.localeCompare(b.name));

          if (this.selectedSite && this.selectedSite.siteOwner && this.selectedSite.siteOwner.siteOwnerUser) {
            const mso = worker.workerUser.workerUser.maintenance.maintenanceSiteOwners.find((m) => {
              return m.siteOwnerUser.skylarkUserId === this.selectedSite.siteOwner.siteOwnerUser.skylarkUserId;
            });
            if (mso) {
              worker.contractorStatus = mso.status;
            }
          }
        });

        this.loadCompetencyCounts(this.siteWorkers);
      }, (error) => {
        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving invited users');
      });
  }

  private loadCompetencyCounts(workers) {
    if (!this.selectedSite || !this.selectedSite.siteOwner || !this.selectedSite.siteOwner.siteOwnerUser) {
      this.hasReliableCompetencyCounts = false;
      return;
    }

    const siteOwnerUserId = this.selectedSite.siteOwner.siteOwnerUser.skylarkUserId;

    const skylarkUserIds = workers
      .map((worker) => this.getWorkerSkylarkUserId(worker))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (!skylarkUserIds.length) {
      this.competencyCountsByUser.clear();
      this.hasReliableCompetencyCounts = workers.length === 0;
      return;
    }

    this.hasReliableCompetencyCounts = false;

    this.competencyService.getCompetencyCountsForSite(skylarkUserIds, this.selectedSiteId, siteOwnerUserId)
      .subscribe((rows) => {
        this.competencyCountsByUser.clear();
        rows.forEach((row) => {
          this.competencyCountsByUser.set(String(row.skylarkUserId), row);
        });
        this.hasReliableCompetencyCounts = true;
      }, () => {
        this.hasReliableCompetencyCounts = false;
        this.notificationService.notify('danger', 'Error', 'Could not load competency data');
      });
  }

  private getWorkerSkylarkUserId(worker): number {
    if (!worker || !worker.workerUser) {
      return null;
    }

    const candidate =
      worker.workerUser.skylarkUserId
      || (worker.workerUser.workerUser && worker.workerUser.workerUser.skylarkUserId)
      || worker.workerUser.id;
    const skylarkUserId = Number(candidate);
    return Number.isFinite(skylarkUserId) && skylarkUserId > 0 ? skylarkUserId : null;
  }

  private getCompetencyCounts(worker): WorkerSiteCompetencyCounts {
    const skylarkUserId = this.getWorkerSkylarkUserId(worker);
    if (!skylarkUserId) {
      return null;
    }
    return this.competencyCountsByUser.get(String(skylarkUserId)) || null;
  }

  public getCompetencyCountLabel(worker): string {
    const counts = this.getCompetencyCounts(worker);
    const total = counts
      ? counts.approvedCount + counts.expiredCount + counts.rejectedCount + counts.pendingCount
      : 0;
    return total + ' ' + (total === 1 ? 'Competency' : 'Competencies');
  }

  public getCompetencyStatusItems(worker): Array<{ count: number; label: string; colorClass: string }> {
    const counts = this.getCompetencyCounts(worker);
    if (!counts) {
      return [];
    }
    return [
      { count: counts.approvedCount, label: 'Approved', colorClass: 'text-success' },
      { count: counts.expiredCount, label: 'Expired', colorClass: 'text-danger' },
      { count: counts.rejectedCount, label: 'Rejected', colorClass: 'text-danger' },
      { count: counts.pendingCount, label: 'Pending', colorClass: 'text-warning' },
    ].filter(function (item) { return item.count > 0; });
  }

  public getCompetencyShortName(name) {
    if (!name) {
      return '';
    }
    const key = name.toLowerCase().trim();
    return InvitedUsersComponent.COMPETENCY_SHORT_NAMES[key] || name;
  }

  public getCompetencyItemClass(status) {
    const statusLower = (status || '').toLowerCase();
    return 'competency-category-cell__item competency-category-cell__item--' + statusLower;
  }

  public resendInvite(worker) {
    if (worker.isSendingInvite || worker.inviteSent) {
      return;
    }

    worker.isSendingInvite = true;

    this.inviteUsersService.resendInvite(worker.login).subscribe(
      (response) => {
        worker.isSendingInvite = false;
        worker.inviteSent = true;
        this.notificationService.notify('success', 'Invite resent to ' + worker.email);
      },
      (error) => {
        worker.isSendingInvite = false;
        this.notificationService.notify('danger', 'Error', 'Failed to resend invite to ' + worker.email);
      }
    );
  }

  public resetColumnOptions() {
    this.tableOptions = {
      visibleColumns: this.togglableColumns.filter((col) => col.default).map((col) => col.displayName),
    };
    this.tableOptions.visibleColumns.push('Induction');
    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.invitedUsers', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.invitedUsers');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter((col) => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach((col) => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }
}
